import type { CSSProperties } from "react";

/**
 * VerificationBadge — Sprint 5t (verifiable content).
 *
 * Reads the `verified_*` frontmatter an AI verification pass writes onto a Page
 * (see the worker's `verify_references` tool + `verify-spec` prompt) and renders
 * one of three states:
 *
 *   - verified  — green ✓ chip, when `verified_at` is set and `unverified_paths`
 *                 is empty ("its file references check out against the snapshot").
 *   - warning   — amber ⚠ chip, when `unverified_paths` has entries (the page
 *                 names paths the snapshot doesn't contain). Wins over verified.
 *   - neutral   — nothing. A page that's never been verified isn't shamed; the
 *                 badge is a cue, not a scold.
 *
 * Styling mirrors <SurfaceBadge> (inline-flex, --kuvio-badge-radius, the Pill
 * colour families). `iconOnly` is the dense rail variant; `warningOnly` suppresses
 * the green state where a clean chip would just be noise (e.g. Active Work).
 */

interface VerificationState {
  kind: "verified" | "warning";
  verifiedAt: string | null;
  against: string | null;
  verifiedCount: number;
  unverifiedCount: number;
}

function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
}

/**
 * Derive the verification state from a Page's frontmatter, or null for the
 * neutral (never-verified) case. Exported so callers can branch before render
 * (e.g. only show a row when there's something to flag).
 */
export function readVerificationState(
  frontmatter: Record<string, unknown> | null | undefined,
): VerificationState | null {
  if (!frontmatter || typeof frontmatter !== "object") return null;
  const verifiedAtRaw = frontmatter.verified_at;
  const verifiedAt = typeof verifiedAtRaw === "string" && verifiedAtRaw ? verifiedAtRaw : null;
  const unverified = asStringArray(frontmatter.unverified_paths);
  const verified = asStringArray(frontmatter.verified_paths);
  // Never verified → neutral.
  if (!verifiedAt && unverified.length === 0) return null;
  const against = typeof frontmatter.verified_against === "string" ? frontmatter.verified_against : null;
  return {
    kind: unverified.length > 0 ? "warning" : "verified",
    verifiedAt,
    against,
    verifiedCount: verified.length,
    unverifiedCount: unverified.length,
  };
}

function timeAgo(iso: string | null): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const mins = Math.floor((Date.now() - t) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const TONE = {
  verified: { bg: "var(--c-green-50)", fg: "var(--c-green-800)", icon: "circle-check" },
  warning: { bg: "var(--c-amber-50)", fg: "var(--c-amber-800)", icon: "alert-triangle" },
} as const;

export interface VerificationBadgeProps {
  frontmatter: Record<string, unknown> | null | undefined;
  /** Tiny icon-only variant for dense surfaces like the rail. */
  iconOnly?: boolean;
  /** Render only the amber warning state (where a green chip would be noise). */
  warningOnly?: boolean;
}

export function VerificationBadge({
  frontmatter,
  iconOnly = false,
  warningOnly = false,
}: VerificationBadgeProps) {
  const state = readVerificationState(frontmatter);
  if (!state) return null;
  if (warningOnly && state.kind !== "warning") return null;

  const tone = TONE[state.kind];
  const tooltip = buildTooltip(state);

  if (iconOnly) {
    return (
      <i
        className={`ti ti-${tone.icon}`}
        title={tooltip}
        aria-label={tooltip}
        style={{ fontSize: 12, color: tone.fg, flexShrink: 0 }}
      />
    );
  }

  const label =
    state.kind === "verified"
      ? `Verified${state.verifiedAt ? ` ${timeAgo(state.verifiedAt)}` : ""}`
      : `${state.unverifiedCount} unverified reference${state.unverifiedCount === 1 ? "" : "s"}`;

  const style: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 10,
    fontWeight: 500,
    fontFamily: "var(--font-sans)",
    background: tone.bg,
    color: tone.fg,
    padding: "1px 6px",
    borderRadius: "var(--kuvio-badge-radius)",
    lineHeight: 1.4,
    whiteSpace: "nowrap",
  };

  return (
    <span style={style} title={tooltip}>
      <i className={`ti ti-${tone.icon}`} style={{ fontSize: 10, verticalAlign: -1 }} aria-hidden="true" />
      {label}
    </span>
  );
}

function buildTooltip(s: VerificationState): string {
  const parts: string[] = [
    s.kind === "verified"
      ? "All path references verified"
      : `${s.unverifiedCount} unverified path reference${s.unverifiedCount === 1 ? "" : "s"}`,
  ];
  if (s.verifiedCount) parts.push(`${s.verifiedCount} verified`);
  if (s.against) parts.push(`snapshot ${s.against}`);
  if (s.verifiedAt) parts.push(`verified ${timeAgo(s.verifiedAt)}`);
  return parts.join(" · ");
}
